import { useCallback, useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const PARALLAX_FACTOR = 0.2; // must stay <= layer side-overflow (0.15) to avoid gaps

// Premium parallax carousel for the landing page only.
// Center slide is emphasised; neighbours peek and dim. Images translate
// slightly slower than their card for depth.
export const DestinationsCarousel = ({ cards }) => {
  // Ensure enough slides for a symmetric loop when few destinations exist.
  const data = cards.length > 0 && cards.length < 5
    ? Array.from({ length: Math.ceil(6 / cards.length) }).flatMap(() => cards)
    : cards;

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: data.length > 1, align: "center", containScroll: false, startIndex: Math.floor(data.length / 2) });
  const layers = useRef([]);
  const slides = useRef([]);
  const [selected, setSelected] = useState(0);
  const [snaps, setSnaps] = useState([]);

  const setNodes = useCallback((api) => {
    slides.current = api.slideNodes();
    layers.current = slides.current.map((n) => n.querySelector(".parallax-layer"));
  }, []);

  const tween = useCallback((api, eventName) => {
    const engine = api.internalEngine();
    const scrollProgress = api.scrollProgress();
    const slidesInView = api.slidesInView();
    const isScroll = eventName === "scroll";

    api.scrollSnapList().forEach((scrollSnap, snapIndex) => {
      let diff = scrollSnap - scrollProgress;
      const inSnap = engine.slideRegistry[snapIndex];
      inSnap.forEach((slideIndex) => {
        if (isScroll && !slidesInView.includes(slideIndex)) return;
        engine.slideLooper.loopPoints.forEach((loop) => {
          const target = loop.target();
          if (slideIndex === loop.index && target !== 0) {
            const sign = Math.sign(target);
            if (sign === -1) diff = scrollSnap - (1 + scrollProgress);
            if (sign === 1) diff = scrollSnap + (1 - scrollProgress);
          }
        });
        const clamped = Math.max(-1, Math.min(1, diff));
        const layer = layers.current[slideIndex];
        if (layer) layer.style.transform = `translateX(${clamped * -PARALLAX_FACTOR * 100}%)`;
        const node = slides.current[slideIndex];
        if (node) {
          const t = Math.min(Math.abs(diff), 1);
          node.style.transform = `scale(${1 - t * 0.12})`;
          node.style.opacity = `${1 - t * 0.55}`;
          node.style.zIndex = `${Math.round((1 - t) * 10)}`;
        }
      });
    });
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    const init = () => { setNodes(emblaApi); setSnaps(emblaApi.scrollSnapList()); };
    init();
    tween(emblaApi);
    onSelect();
    emblaApi.on("reInit", init).on("reInit", tween).on("scroll", tween).on("slideFocus", tween).on("select", onSelect);
    return () => {
      emblaApi.off("reInit", init).off("reInit", tween).off("scroll", tween).off("slideFocus", tween).off("select", onSelect);
    };
  }, [emblaApi, setNodes, tween]);

  return (
    <div className="relative mt-12" data-testid="destinations-carousel">
      <div className="overflow-hidden py-4" ref={emblaRef}>
        <div className="flex touch-pan-y">
          {data.map((d, idx) => {
            const inner = (
              <>
                <div className="absolute inset-0 overflow-hidden">
                  <div className="parallax-layer absolute inset-y-0 will-change-transform" style={{ left: "-15%", width: "130%" }}>
                    {d.img ? (
                      <img src={d.img} alt={d.title} loading="lazy" className="w-full h-full object-cover" />
                    ) : <div className="w-full h-full bg-[#E4DCCF]" />}
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                </div>
                <div className="relative z-10 mt-auto p-7 md:p-9 text-white">
                  <h3 className="font-serif-display text-3xl md:text-4xl leading-tight">{d.title}</h3>
                  {d.subtitle && <p className="text-[11px] uppercase tracking-[0.2em] text-[#E4DCCF] mt-2">{d.subtitle}</p>}
                  {d.to && <span className="mt-4 inline-flex items-center gap-2 text-sm border-b border-white/50 pb-1">Découvrir cette destination →</span>}
                </div>
              </>
            );
            const cls = "relative flex flex-col aspect-[4/5] md:aspect-[3/4] overflow-hidden rounded-2xl bg-[#E4DCCF] transition-[transform,opacity] duration-300 ease-out";
            return (
              <div key={`${d.key}-${idx}`} className="flex-[0_0_78%] sm:flex-[0_0_58%] md:flex-[0_0_44%] lg:flex-[0_0_36%] min-w-0 px-3">
                {d.to ? (
                  <Link to={d.to} data-testid={`carousel-card-${d.key}`} className={`group ${cls}`}>{inner}</Link>
                ) : (
                  <article data-testid={`carousel-card-${d.key}`} className={cls}>{inner}</article>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {data.length > 1 && (
        <>
          <button onClick={() => emblaApi?.scrollPrev()} data-testid="carousel-prev" aria-label="Précédent"
            className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white shadow-[0_10px_30px_-10px_rgba(0,0,0,0.4)] flex items-center justify-center hover:bg-[#1A1B18] hover:text-white transition-colors">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <button onClick={() => emblaApi?.scrollNext()} data-testid="carousel-next" aria-label="Suivant"
            className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white shadow-[0_10px_30px_-10px_rgba(0,0,0,0.4)] flex items-center justify-center hover:bg-[#1A1B18] hover:text-white transition-colors">
            <ArrowRight className="h-4 w-4" />
          </button>
          <div className="flex justify-center gap-2 mt-8" data-testid="carousel-dots">
            {snaps.map((_, i) => (
              <button key={i} onClick={() => emblaApi?.scrollTo(i)} aria-label={`Aller à la destination ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${i === selected ? "w-6 bg-[#96836B]" : "w-1.5 bg-[#E4DCCF] hover:bg-[#96836B]/60"}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default DestinationsCarousel;
